import {announce} from '@github-ui/aria-live'
import type {CreateMilestoneInput} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import {type Dispatch, type SetStateAction, useCallback, useRef, useState} from 'react'

import {MilestoneForm} from './components/MilestoneForm'
import {LABELS} from './constants/labels'
import {type CreatedMilestone, useCreateRepositoryMilestone} from './use-create-repository-milestone'

export type InlineMilestoneFormProps = {
  repository: InlineMilestoneFormRepository
  initialTitle?: string
  onCancel: () => void
  onPendingChange?: (isPending: boolean) => void
  onSuccess: (milestone: CreatedMilestone) => void | Promise<void>
}

export type InlineMilestoneFormRepository = {
  id: string
  nameWithOwner: string
  viewerCanCreateMilestones: boolean
}

export function InlineMilestoneForm({
  repository,
  initialTitle,
  onCancel,
  onPendingChange,
  onSuccess,
}: InlineMilestoneFormProps) {
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const showSubmissionError = useCallback((message: string | null) => {
    setSubmissionErrors(message)
    if (message) {
      announce(message, {assertive: true, element: formRef.current ?? undefined})
    }
  }, [])

  const handleSuccess = useCallback(
    async (milestone: CreatedMilestone) => {
      setIsComplete(true)
      await onSuccess(milestone)
    },
    [onSuccess],
  )

  const createMilestone = useCreateRepositoryMilestone({
    repositoryId: repository.id,
    repositoryNameWithOwner: repository.nameWithOwner,
    setSubmissionErrors: showSubmissionError,
    onSuccess: handleSuccess,
  })

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      showSubmissionError(null)

      if (!repository.viewerCanCreateMilestones) {
        showSubmissionError(LABELS.milestoneCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      onPendingChange?.(true)
      createMilestone(input, isSubmitting => {
        setIsSubmitting(isSubmitting)
        onPendingChange?.(isSubmitting)
      })
    },
    [createMilestone, onPendingChange, repository.viewerCanCreateMilestones, showSubmissionError],
  )

  return (
    <MilestoneForm
      repository={repository}
      onCancel={onCancel}
      onSubmit={handleFormSubmit}
      submissionErrors={submissionErrors}
      initialValues={initialTitle === undefined ? undefined : {title: initialTitle}}
      formRef={formRef}
      isComplete={isComplete}
    />
  )
}
